import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Home2,
  ShieldSearch,
  Chart21,
  Sms,
  Global,
  GlobalSearch,
  Routing2,
  Flash,
  Setting2,
  Messages2,
  HamburgerMenu,
  ArrowSwapHorizontal,
  Logout,
  Add,
  Microphone2,
  SecuritySafe,
} from "iconsax-reactjs";
import tokenManager from "../../utils/tokenManager";
import { useUser } from "../../contexts/UserContext";
import { useLicense } from "../../hooks/useLicense";

const Sidebar = () => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const profileRef = useRef(null);
  const { refreshUser } = useUser();
  const {
    hasRecordingStudio,
    accessType,
    loading: licenseLoading,
  } = useLicense();

  const currentUser = tokenManager.getUser();
  const accounts = tokenManager.getAccounts();

  // Redirect to login if session is broken
  useEffect(() => {
    if (!currentUser || !tokenManager.isAuthenticated()) {
      console.warn("Invalid session detected, redirecting to login");
      tokenManager.clearCurrentSession();
      navigate("/login", { replace: true });
    }
  }, [currentUser, navigate]);

  const RECORDING_STUDIO_URL = process.env.REACT_APP_RECORDING_STUDIO_URL;

  const handleLogout = async () => {
    try {
      tokenManager.removeTokens();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleSwitchAccount = (userId) => {
    const success = tokenManager.switchAccount(userId);
    if (success) {
      refreshUser();
      setIsProfileDropdownOpen(false);
      window.location.reload();
    } else {
      tokenManager.clearCurrentSession();
      navigate("/login", { replace: true });
    }
  };

  const handleAddAccount = () => {
    tokenManager.removeTokens();
    navigate("/login?mode=add");
    setIsProfileDropdownOpen(false);
  };

  const handleSwitchToRecordingStudio = () => {
    if (RECORDING_STUDIO_URL) {
      window.location.href = RECORDING_STUDIO_URL;
    }
  };

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
    setIsProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    if (!isCollapsed) {
      setIsProfileDropdownOpen((v) => !v);
    } else {
      setIsProfileDropdownOpen(true);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        profileRef.current &&
        !profileRef.current.contains(event.target)
      ) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isCollapsed) setIsProfileDropdownOpen(false);
  }, [isCollapsed]);

  if (!currentUser) return null;

  // ── Main nav (Dark Web Monitor) ────────────────────────────────
  const navItems = [
    { to: "/overview", icon: Home2, label: "Dashboard", end: true },
    { to: "/darkweb/breaches", icon: ShieldSearch, label: "Breach Database" },
    { to: "/darkweb/breakdown", icon: Chart21, label: "Breach Breakdown" },
    { to: "/darkweb/emails", icon: Sms, label: "Monitored Emails" },
    { to: "/darkweb/domains", icon: Global, label: "Monitored Domains" },
    {
      to: "/darkweb/lookalikes",
      icon: GlobalSearch,
      label: "Domain Lookalikes",
    },
    { to: "/darkweb/ips", icon: Routing2, label: "Monitored IPs" },
    { to: "/darkweb/incidents", icon: Flash, label: "Incident Response" },
  ];

  const midNavItems = [{ to: "/settings", icon: Setting2, label: "Settings" }];

  const showCrossAppLink =
    !licenseLoading && hasRecordingStudio && !!RECORDING_STUDIO_URL;

  const profileMenuItems = [
    {
      icon: Setting2,
      label: "Account settings",
      onClick: () => {
        navigate("/settings");
        setIsProfileDropdownOpen(false);
      },
    },
    ...(showCrossAppLink
      ? [
          {
            icon: ArrowSwapHorizontal,
            label: "Switch to Recording Studio",
            onClick: handleSwitchToRecordingStudio,
          },
        ]
      : []),
  ];

  return (
    <div
      className={`${
        isCollapsed ? "!w-20" : "!w-80"
      } !bg-[#0C0E12] !border-r !border-[#22262F] !min-h-screen !flex !flex-col !transition-all !duration-500 !ease-in-out !relative !overflow-visible`}
    >
      {/* Logo */}
      <div
        className={`!p-4 !transition-all !duration-500 ${
          isCollapsed ? "!px-2" : ""
        }`}
      >
        <div className="!flex !justify-center">
          <div className="!flex !items-center !justify-center !overflow-hidden !gap-2">
            <SecuritySafe
              className={`!text-red-500 !transition-all !duration-500 ${
                isCollapsed ? "!h-8 !w-8" : "!h-10 !w-10"
              }`}
              variant="Bold"
            />
            {!isCollapsed && (
              <div className="!flex !flex-col !leading-tight">
                <span className="!text-white !font-bold !text-base !whitespace-nowrap">
                  Dark Web
                </span>
                <span className="!text-red-500 !font-semibold !text-xs !tracking-wider !whitespace-nowrap">
                  MONITOR
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={toggleSidebar}
        className="!rounded-sm !px-2.5 !shadow-md hover:!shadow-lg !transition-all !duration-300 !z-10 !transform hover:!scale-105 !mx-5"
        title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <HamburgerMenu
          className={`!h-5 !w-5 !text-white !transition-transform !duration-300 ${
            isCollapsed ? "!rotate-180" : ""
          }`}
        />
      </button>

      {/* Main nav */}
      <nav className="!mt-4 !mx-5">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              `!flex !items-center !mb-1 ${
                isCollapsed ? "!justify-center !px-2" : "!px-4"
              } !py-3 ${
                isActive
                  ? "!bg-[#22262F] !text-[#CECFD2] !font-bold !rounded-md"
                  : "!text-[#CECFD2] hover:!bg-[#22262F]"
              } !transition-all !duration-300 !ease-in-out !rounded-md`
            }
            style={{ textDecoration: "none" }}
            title={isCollapsed ? item.label : ""}
          >
            <div
              className={`!flex !items-center !justify-center ${
                isCollapsed ? "!w-5" : "!w-5 !mr-3"
              } !transition-all !duration-300`}
            >
              <item.icon className="!h-5 !w-5 !text-[#61656C] !transition-all !duration-300" />
            </div>
            <div
              className={`!overflow-hidden !transition-all !duration-500 !ease-in-out ${
                isCollapsed ? "!w-0 !opacity-0" : "!w-auto !opacity-100"
              }`}
            >
              <span className="!whitespace-nowrap">{item.label}</span>
            </div>
          </NavLink>
        ))}

        {/* Cross-app switch — only if user has Recording Studio access */}
        {showCrossAppLink && (
          <button
            onClick={handleSwitchToRecordingStudio}
            className={`!w-full !flex !items-center !mb-1 ${
              isCollapsed ? "!justify-center !px-2" : "!px-4"
            } !py-3 !text-[#CECFD2] hover:!bg-[#22262F] !transition-all !duration-300 !ease-in-out !rounded-md !bg-transparent !border-0 !cursor-pointer`}
            title={isCollapsed ? "Open Recording Studio" : ""}
          >
            <div
              className={`!flex !items-center !justify-center ${
                isCollapsed ? "!w-5" : "!w-5 !mr-3"
              } !transition-all !duration-300`}
            >
              <Microphone2 className="!h-5 !w-5 !text-violet-400" />
            </div>
            <div
              className={`!overflow-hidden !transition-all !duration-500 !ease-in-out ${
                isCollapsed
                  ? "!w-0 !opacity-0"
                  : "!w-auto !opacity-100 !flex !items-center !gap-2"
              }`}
            >
              <span className="!whitespace-nowrap">Recording Studio</span>
              <span className="!text-[10px] !text-[#61656C] !uppercase !tracking-wide">
                ↗
              </span>
            </div>
          </button>
        )}
      </nav>

      {/* Mid nav */}
      <div className="!mt-4 !mx-5">
        <div
          className={`!border-t !border-[#22262F] !transition-all !duration-300 ${
            isCollapsed ? "!mx-2" : "!mx-0"
          }`}
        />
        <div className="!pt-2 !pb-2">
          {midNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `!flex !items-center !mb-1 ${
                  isCollapsed ? "!justify-center !px-2" : "!px-4"
                } !py-3 ${
                  isActive
                    ? "!bg-[#22262F] !text-[#CECFD2] !font-bold !rounded-md"
                    : "!text-[#CECFD2] hover:!bg-[#22262F]"
                } !transition-all !duration-300 !ease-in-out !rounded-md`
              }
              style={{ textDecoration: "none" }}
              title={isCollapsed ? item.label : ""}
            >
              <div
                className={`!flex !items-center !justify-center ${
                  isCollapsed ? "!w-5" : "!w-5 !mr-3"
                } !transition-all !duration-300`}
              >
                <item.icon className="!h-5 !w-5 !text-[#61656C]" />
              </div>
              <div
                className={`!overflow-hidden !transition-all !duration-500 !ease-in-out ${
                  isCollapsed ? "!w-0 !opacity-0" : "!w-auto !opacity-100"
                }`}
              >
                <span className="!whitespace-nowrap">{item.label}</span>
              </div>
            </NavLink>
          ))}
        </div>
      </div>

      {/* User profile + dropdown */}
      <div className="!mt-auto !relative">
        <div
          className={`!p-4 !border-t !border-[#22262F] !transition-all !duration-500 ${
            isCollapsed ? "!px-2" : ""
          }`}
        >
          <div className="!relative">
            {isCollapsed ? (
              <div
                ref={profileRef}
                onClick={toggleProfileDropdown}
                className="!flex !justify-center"
              >
                {currentUser?.profileImageUrl ? (
                  <img
                    src={currentUser.profileImageUrl}
                    alt="Profile"
                    className="!w-10 !h-10 !rounded-full !cursor-pointer hover:!opacity-90 !transition-all !duration-300 !transform hover:!scale-105"
                  />
                ) : (
                  <div className="!w-10 !h-10 !rounded-full !bg-gray-300 !flex !items-center !justify-center !cursor-pointer hover:!bg-gray-400 !transition-all !duration-300 !transform hover:!scale-105">
                    <span className="!text-sm !font-medium !text-gray-700">
                      {currentUser?.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("") || "U"}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div
                ref={profileRef}
                onClick={toggleProfileDropdown}
                className="!flex !items-center !bg-[#13161B] !rounded-xl !border !border-[#22262F] !p-5 !transition-all !duration-300 !cursor-pointer hover:!bg-[#1A1D23] hover:!border-[#2A2E37]"
              >
                {currentUser?.profileImageUrl ? (
                  <img
                    src={currentUser.profileImageUrl}
                    alt="Profile"
                    className="!w-10 !h-10 !rounded-full !mr-3"
                  />
                ) : (
                  <div className="!w-10 !h-10 !rounded-full !bg-gray-300 !flex !items-center !justify-center !mr-3">
                    <span className="!text-sm !font-medium !text-gray-700">
                      {currentUser?.name
                        ?.split(" ")
                        .map((n) => n[0])
                        .join("") || "U"}
                    </span>
                  </div>
                )}
                <div className="!flex-1 !min-w-0 !overflow-hidden">
                  <div className="!text-sm !font-medium !text-[#F7F7F7] !truncate !whitespace-nowrap">
                    {currentUser?.name || "User"}
                  </div>
                  <div className="!text-xs !text-[#94979C] !truncate !whitespace-nowrap">
                    {currentUser?.email || "email@example.com"}
                  </div>
                  {/* Access badges */}
                  {!licenseLoading && accessType && (
                    <div className="!flex !gap-1 !mt-1">
                      {(accessType === "full" ||
                        accessType === "darkweb_monitor") && (
                        <span className="!px-1.5 !py-0.5 !text-[9px] !rounded !bg-red-600/20 !text-red-300 !border !border-red-700/50">
                          DWM
                        </span>
                      )}
                      {(accessType === "full" ||
                        accessType === "recording_studio") && (
                        <span className="!px-1.5 !py-0.5 !text-[9px] !rounded !bg-emerald-600/20 !text-emerald-300 !border !border-emerald-700/50">
                          RS
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {isProfileDropdownOpen && (
              <div
                ref={dropdownRef}
                className={`!absolute ${
                  isCollapsed ? "!left-full !ml-2" : "!left-0 !right-0"
                } !bottom-0 !bg-[#0C0E12] !border !border-[#22262F] !rounded-xl !shadow-2xl !overflow-hidden !z-50 ${
                  isCollapsed ? "!w-64" : "!w-full"
                }`}
              >
                <div className="!py-2 !px-4">
                  {profileMenuItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={item.onClick}
                      className="!w-full !flex !items-center !px-4 !py-2.5 !text-sm !text-[#CECFD2] hover:!bg-[#22262F] !transition-colors !duration-200 !rounded"
                    >
                      <item.icon className="!w-4 !h-4 !mr-3 !text-[#61656C]" />
                      {item.label}
                    </button>
                  ))}
                </div>

                <div className="!border-t !border-[#22262F]" />

                <div className="!py-2">
                  <div className="!px-4 !py-2 !text-xs !font-medium !text-[#94979C] !uppercase !tracking-wider">
                    Switch account
                  </div>
                  <div className="!px-4">
                    {accounts.map((account, index) => (
                      <button
                        key={index}
                        onClick={() => handleSwitchAccount(account.id)}
                        className={`!w-full !flex !items-center !px-4 !py-2.5 !text-sm !rounded-lg hover:!bg-[#22262F] !transition-colors !duration-200 !mb-2 ${
                          account.id === currentUser?.id ? "!bg-[#22262F]" : ""
                        }`}
                      >
                        {account.profileImageUrl ? (
                          <img
                            src={account.profileImageUrl}
                            alt="Account"
                            className="!w-8 !h-8 !rounded-full !mr-3"
                          />
                        ) : (
                          <div className="!w-8 !h-8 !rounded-full !bg-gray-300 !flex !items-center !justify-center !mr-3">
                            <span className="!text-xs !font-medium !text-gray-700">
                              {account.name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("") || "U"}
                            </span>
                          </div>
                        )}
                        <div className="!flex-1 !text-left">
                          <div className="!text-[#CECFD2] !font-medium">
                            {account.name}
                          </div>
                          <div className="!text-xs !text-[#94979C]">
                            {account.email}
                          </div>
                        </div>
                        {account.id === currentUser?.id && (
                          <div className="!w-2 !h-2 !bg-red-500 !rounded-full" />
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="!px-4">
                    <button
                      onClick={handleAddAccount}
                      className="!w-full !flex !items-center !px-4 !py-2.5 !justify-center !text-sm !text-[#CECFD2] hover:!bg-[#22262F] !transition-colors !duration-200 !text-center !border !border-[#22262F] !mt-2 !rounded-lg"
                    >
                      <Add className="!w-4 !h-4 !mr-3 !text-[#61656C]" />
                      Add account
                    </button>
                  </div>
                </div>

                <div className="!border-t !border-[#22262F]" />

                <div className="!py-2 !px-2">
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsProfileDropdownOpen(false);
                    }}
                    className="!w-full !flex !items-center !px-4 !py-2.5 !text-sm !text-[#CECFD2] hover:!bg-[#22262F] !transition-colors !duration-200 !rounded"
                  >
                    <Logout className="!w-4 !h-4 !mr-3 !text-[#61656C]" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
